import {
  getAuth,
  type Auth,
  type User as FirebaseUser,
  connectAuthEmulator,
  signInWithCustomToken,
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import type { Configuration } from "@outverse-video/outverse-api-client-lib";
import { building } from "$app/environment";
import { invalidateAll } from "$app/navigation";
import { env } from "$env/dynamic/public";
import { sveltekitApiUrl } from "$utils/urls";
import {
  PUBLIC_ENV,
  PUBLIC_FIREBASE_API_KEY,
  PUBLIC_FIREBASE_APP_ID,
  PUBLIC_FIREBASE_AUTH_DOMAIN,
  PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  PUBLIC_FIREBASE_PROJECT_ID,
  PUBLIC_FIREBASE_STORAGE_BUCKET,
} from "$env/static/public";
import { createUser } from "$utils/auth";

// firebase public config
const firebaseConfig = {
  apiKey: PUBLIC_FIREBASE_API_KEY,
  authDomain: PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: PUBLIC_FIREBASE_APP_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

export let firebaseAuth: Auth;

if (!building) {
  firebaseAuth = getAuth(firebaseApp);

  const emulatorUrl = env.PUBLIC_FIREBASE_EMULATOR_AUTH_URL;
  if (PUBLIC_ENV === "test" && firebaseAuth && emulatorUrl) {
    console.warn(`Using Firebase Emulator: ${emulatorUrl}`);
    connectAuthEmulator(firebaseAuth, emulatorUrl);
  }
}

if (typeof window !== "undefined") {
  (window as any).firebase = firebaseApp;
  (window as any).firebase.auth = () => ({
    signInWithCustomToken: (customToken: string) =>
      signInWithCustomToken(firebaseAuth, customToken),
  });
}

/**
 * Set the `token` as a cookie on the sveltekit server
 *
 * Necessary to pass the users auth token along for future SSR requests.
 *
 */
export async function setToken(token: string) {
  return await (
    await fetch(`${sveltekitApiUrl()}/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
      },
      body: JSON.stringify({ token }),
    })
  ).json();
}

let tokenIsRefreshing: Promise<void> | null = null;
/** Refresh the firebase token and set the new token as a cookie
 *
 * This blocks on setting the cookie to ensure cookie is set for SSR + matches localstorage.
 */
export function refreshFirebaseToken(firebaseUser: FirebaseUser, config: Configuration) {
  if (tokenIsRefreshing) {
    return tokenIsRefreshing;
  }

  tokenIsRefreshing = new Promise<void>((resolve, reject) => {
    firebaseUser
      .getIdToken()
      .then((token) => {
        config.apiKey = token;
        // wait for token to be set before resolving
        resolve();
        setToken(token).then(({ alreadySet }) => {
          // if token wasn't previously set, we need to rerun all the load
          // functions to ensure they have authed content
          // e.g., when user first logs in, or they delete cookies
          if (!alreadySet) {
            invalidateAll();
          }
        });
      })
      .catch((err) => reject(err))
      .finally(() => {
        tokenIsRefreshing = null;
      });
  });

  return tokenIsRefreshing;
}

export async function createUserWithCustomToken(token: string) {
  const userCred = await signInWithCustomToken(firebaseAuth, token);
  return await createUser(userCred, "");
}
